import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, Stack, Chip, Button, Tabs, Tab, Tooltip,
  Select, MenuItem, FormControl, InputLabel, TextField, Switch, IconButton,
  Skeleton, Alert, Divider,
  FormControlLabel, alpha, useTheme,
} from '@mui/material';
import {
  Sparkles, RefreshCw, Plus, Trash2, Pencil, Info,
  Share2, MessageCircle, Search as SearchIcon, ShoppingCart, Settings2, Image,
  FileText,
} from 'lucide-react';
import axios from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions, DeleteConfirmDialog } from '../components/common/modal';
import { notify } from '../utils/notificationService';

// ── Category + status presentation ──────────────────────────────────────────
const CATEGORY_META = {
  social: { label: 'Social', color: '#8b5cf6', icon: Share2 },
  whatsapp: { label: 'WhatsApp', color: '#25D366', icon: MessageCircle },
  seo: { label: 'SEO / Web', color: '#0ea5e9', icon: SearchIcon },
  sales: { label: 'Sales', color: '#f59e0b', icon: ShoppingCart },
  ops: { label: 'Ops', color: '#64748b', icon: Settings2 },
  content: { label: 'Content', color: '#ec4899', icon: Image },
};
const catMeta = (c) => CATEGORY_META[c] || { label: c, color: '#64748b', icon: Settings2 };
const STATUSES = [
  { value: 'pending', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'skipped', label: 'Skipped' },
];
const PRIORITY_COLOR = { high: '#ef4444', medium: '#f59e0b', low: '#94a3b8' };
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const assigneeName = (a) =>
  !a ? '' : (a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim());

const todayYmd = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());

/* ─────────────────────────── Scorecard strip ─────────────────────────────*/
const ScoreStat = ({ label, value, sub, color }) => (
  <Card sx={{ p: 2, borderRadius: 3, flex: 1, minWidth: 120 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 800, color: color || 'text.primary', lineHeight: 1.2 }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{sub}</Typography>}
  </Card>
);

const TaskRow = ({ item, onStatus }) => {
  const theme = useTheme();
  const meta = catMeta(item.category);
  const Icon = meta.icon;
  const done = item.status === 'done';
  const skipped = item.status === 'skipped';
  const ctx = item.data_context || {};
  const ctxText = Object.keys(ctx).length
    ? Object.entries(ctx).map(([k, v]) => `${k}: ${v}`).join(' · ')
    : '';

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      justifyContent="space-between"
      sx={{
        p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`,
        bgcolor: done ? alpha(theme.palette.success.main, 0.05) : 'background.paper',
        opacity: skipped ? 0.6 : 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 1.5, flexShrink: 0, mt: 0.25,
          bgcolor: alpha(meta.color, 0.14), color: meta.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={16} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{
              fontSize: 14, fontWeight: 700,
              textDecoration: done ? 'line-through' : 'none',
              color: done ? 'text.secondary' : 'text.primary',
              lineHeight: 1.35,
            }}>
              {item.title}
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: PRIORITY_COLOR[item.priority] || '#94a3b8',
                  ml: 1,
                  verticalAlign: 'middle',
                }}
              />
            </Typography>
            {item.source === 'dynamic' && (
              <Tooltip title={ctxText ? `Why: ${ctxText}` : 'Generated from live data'}>
                <Chip size="small" icon={<Sparkles size={12} />} label="Auto"
                  sx={{ height: 20, fontSize: 10, fontWeight: 800,
                    bgcolor: alpha('#8b5cf6', 0.12), color: '#8b5cf6' }} />
              </Tooltip>
            )}
          </Box>
          {item.detail && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{item.detail}</Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
            <Chip size="small" label={meta.label} sx={{ height: 18, fontSize: 10, fontWeight: 700,
              bgcolor: alpha(meta.color, 0.10), color: meta.color }} />
            {assigneeName(item.assignee) && (
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>· {assigneeName(item.assignee)}</Typography>
            )}
            {item.link && (
              <Button size="small" href={`#${item.link}`} onClick={(e) => { e.preventDefault(); window.location.hash = ''; window.location.pathname = item.link; }}
                sx={{ minWidth: 0, p: 0, fontSize: 11, textTransform: 'none' }}>
                Open ↗
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>

      <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 }, flexShrink: 0, width: { xs: '100%', sm: 'auto' }, alignSelf: { xs: 'stretch', sm: 'auto' } }}>
        <Select
          value={item.status}
          onChange={(e) => onStatus(item, e.target.value)}
          sx={{ fontSize: 12.5, fontWeight: 700,
            '& .MuiSelect-select': { py: 0.75 } }}
        >
          {STATUSES.map((s) => <MenuItem key={s.value} value={s.value} sx={{ fontSize: 13 }}>{s.label}</MenuItem>)}
        </Select>
      </FormControl>
    </Stack>
  );
};

/* ─────────────────────────── Today / Week list ────────────────────────────*/
const ChecklistTab = ({ scope, date }) => {
  const [loading, setLoading] = useState(true);
  const [instances, setInstances] = useState([]);
  const [scorecard, setScorecard] = useState(null);
  const [generating, setGenerating] = useState(false);
  const theme = useTheme();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inst, score] = await Promise.all([
        axios.get(`/admin/sop/instances`, { params: { scope, date }, skipNotify: true }),
        axios.get(`/admin/sop/scorecard`, { params: { date }, skipNotify: true }),
      ]);
      setInstances(inst.data.instances || []);
      setScorecard(score.data || null);
    } catch (e) { /* interceptor notifies */ }
    setLoading(false);
  }, [scope, date]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (item, status) => {
    let note = item.note;
    if (status === 'skipped') {
      note = window.prompt('Reason for skipping? (optional)', '') || '';
    }
    // optimistic
    setInstances((prev) => prev.map((x) => x._id === item._id ? { ...x, status, note } : x));
    try {
      await axios.patch(`/admin/sop/instances/${item._id}`, { status, note }, { skipNotify: 'success' });
      load();
    } catch (e) { load(); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`/admin/sop/generate`, scope === 'day' ? { frequencies: 'daily' } : {});
      await load();
    } catch (e) { /* notified */ }
    setGenerating(false);
  };

  // group by category for "day", by weekday for "week"
  const grouped = useMemo(() => {
    if (scope === 'week') {
      const byDay = {};
      instances.forEach((i) => {
        const d = i.due_date ? new Date(i.due_date) : null;
        const k = d
          ? new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', weekday: 'short' }).format(d)
          : 'Other';
        (byDay[k] = byDay[k] || []).push(i);
      });
      return WEEKDAYS.filter((d) => byDay[d]).map((d) => [d, byDay[d]]);
    }
    const byCat = {};
    instances.forEach((i) => { (byCat[i.category] = byCat[i.category] || []).push(i); });
    return Object.keys(byCat).sort().map((c) => [c, byCat[c]]);
  }, [instances, scope]);

  const score = scope === 'week' ? scorecard?.week : scorecard?.today;

  return (
    <Box>
      {/* Scorecard + actions */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
        <ScoreStat label={scope === 'week' ? 'This week' : 'Today'} value={`${score?.pct ?? 0}%`} sub="completion" color={theme.palette.success.main} />
        <ScoreStat label="Done" value={score?.done ?? 0} sub={`of ${score?.total ?? 0} tasks`} />
        <ScoreStat label="To do" value={score?.pending ?? 0} color={theme.palette.warning.main} />
        <ScoreStat label="In progress" value={score?.in_progress ?? 0} color={theme.palette.info.main} />
        <Card sx={{ p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="contained" startIcon={<RefreshCw size={16} />} onClick={generate} disabled={generating}
            fullWidth
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, py: 1 }}>
            {generating ? 'Generating…' : 'Generate now'}
          </Button>
        </Card>
      </Stack>

      {scorecard && !scorecard.dynamic_enabled && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Auto-generated (data-driven) SOPs are currently <b>OFF</b>. Existing tasks stay visible; turn it back on in
          Settings → SOP Automation.
        </Alert>
      )}

      {loading ? (
        <Stack spacing={1}>{[...Array(5)].map((_, i) => <Skeleton key={i} variant="rounded" height={64} />)}</Stack>
      ) : instances.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No SOP tasks for this {scope === 'week' ? 'week' : 'day'} yet. Click <b>Generate now</b> to materialise them.
        </Alert>
      ) : (
        <Stack spacing={2.5}>
          {grouped.map(([key, list]) => {
            const isWeek = scope === 'week';
            const meta = isWeek ? null : catMeta(key);
            return (
              <Box key={key}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1,
                  color: isWeek ? 'text.secondary' : meta.color, mb: 1 }}>
                  {isWeek ? key : meta.label} · {list.length}
                </Typography>
                <Stack spacing={1}>
                  {list.map((item) => <TaskRow key={item._id} item={item} onStatus={onStatus} />)}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

/* ─────────────────────────── Templates tab ────────────────────────────────*/
const emptyTemplate = {
  title: '', description: '', category: 'ops', type: 'static', frequency: 'daily',
  weekday: '', priority: 'medium', brand_pillar: 'none', estimated_minutes: 15,
  rule_key: '', rule_params: '{}', active: true,
};

const TemplatesTab = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [enums, setEnums] = useState({ CATEGORIES: [], FREQUENCIES: [], PRIORITIES: [] });
  const [dialog, setDialog] = useState(null); // template being edited or 'new'
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const theme = useTheme();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/admin/sop/templates', { skipNotify: true });
      setTemplates(data.templates || []);
      setRules(data.rules || []);
      setEnums(data.enums || enums);
    } catch (e) { /* notified */ }
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openNew = () => setDialog({ ...emptyTemplate });
  const openEdit = (t) => setDialog({
    ...t,
    weekday: t.weekday == null ? '' : t.weekday,
    rule_params: JSON.stringify(t.rule_params || {}, null, 0),
  });

  const save = async () => {
    const d = dialog;
    let rule_params = {};
    try { rule_params = d.rule_params ? JSON.parse(d.rule_params) : {}; }
    catch { notify({ message: 'Rule params must be valid JSON', severity: 'error' }); return; }
    const payload = {
      title: d.title, description: d.description, category: d.category, type: d.type,
      frequency: d.frequency, priority: d.priority, brand_pillar: d.brand_pillar,
      estimated_minutes: Number(d.estimated_minutes) || 0, active: d.active,
      weekday: d.weekday === '' ? null : Number(d.weekday),
      rule_key: d.type === 'dynamic' ? d.rule_key : '',
      rule_params: d.type === 'dynamic' ? rule_params : {},
    };
    try {
      if (d._id) await axios.put(`/admin/sop/templates/${d._id}`, payload);
      else await axios.post('/admin/sop/templates', payload);
      setDialog(null);
      load();
    } catch (e) { /* notified */ }
  };

  const toggleActive = async (t) => {
    try { await axios.put(`/admin/sop/templates/${t._id}`, { active: !t.active }, { skipNotify: 'success' }); load(); }
    catch (e) { /* notified */ }
  };

  const remove = (t) => {
    setDeleteTarget(t);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`/admin/sop/templates/${deleteTarget._id}`);
      setDeleteTarget(null);
      load();
    } catch (e) { /* notified */ }
    setDeleting(false);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1 }}>
          Define the recurring & data-driven SOPs. Add, edit or disable without a deploy.
        </Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openNew}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>New template</Button>
      </Stack>

      {loading ? (
        <Stack spacing={1}>{[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={56} />)}</Stack>
      ) : (
        <Stack spacing={1.25}>
          {templates.map((t) => {
            const meta = catMeta(t.category);
            return (
              <Stack key={t._id} direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, opacity: t.active ? 1 : 0.55, bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Chip size="small" label={meta.label} sx={{ height: 20, fontSize: 10, fontWeight: 700,
                    bgcolor: alpha(meta.color, 0.12), color: meta.color, minWidth: 72, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, wordBreak: 'break-word' }}>{t.title}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                      {t.frequency}{t.weekday != null ? ` · ${WEEKDAYS[t.weekday]}` : ''} · {t.type}
                      {t.type === 'dynamic' ? ` · ${t.rule_key}` : ''}{t.is_system ? ' · system' : ''}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-end' }} sx={{ pt: { xs: 0.75, sm: 0 }, borderTop: { xs: `1px dashed ${alpha(theme.palette.divider, 0.5)}`, sm: 'none' } }}>
                  <Chip size="small" label={t.type === 'dynamic' ? 'Auto' : 'Static'}
                    sx={{ height: 20, fontSize: 10, fontWeight: 800,
                      bgcolor: alpha(t.type === 'dynamic' ? '#8b5cf6' : '#64748b', 0.12),
                      color: t.type === 'dynamic' ? '#8b5cf6' : '#64748b' }} />
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Switch size="small" checked={t.active} onChange={() => toggleActive(t)} />
                    <IconButton aria-label="Edit" size="small" onClick={() => openEdit(t)}><Pencil size={15} /></IconButton>
                    <Tooltip title={t.is_system ? 'System template — disable instead of deleting' : 'Delete'}>
                      <span>
                        <IconButton aria-label="Delete" size="small" disabled={t.is_system} onClick={() => remove(t)}
                          sx={{ color: t.is_system ? 'text.disabled' : 'error.main' }}>
                          <Trash2 size={15} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}

      {/* Editor dialog */}
      {/* Editor dialog */}
      <AdminDialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm">
        <AdminDialogHeader
          title={dialog?._id ? 'Edit SOP Template' : 'New SOP Template'}
          subtitle="Configure operational checklists and frequency triggers"
          icon={FileText}
          onClose={() => setDialog(null)}
        />
        <AdminDialogContent dividers>
          {dialog && (
            <Stack spacing={2.5} sx={{ mt: 0.5 }}>
              <TextField label="Title" fullWidth value={dialog.title}
                onChange={(e) => setDialog({ ...dialog, title: e.target.value })} />
              <TextField label="Description / instruction" fullWidth multiline minRows={2}
                value={dialog.description} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} />
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select label="Category" value={dialog.category}
                    onChange={(e) => setDialog({ ...dialog, category: e.target.value })}>
                    {(enums.CATEGORIES || []).map((c) => <MenuItem key={c} value={c}>{catMeta(c).label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={dialog.type}
                    onChange={(e) => setDialog({ ...dialog, type: e.target.value })}>
                    <MenuItem value="static">Static (recurring)</MenuItem>
                    <MenuItem value="dynamic">Dynamic (data-driven)</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select label="Frequency" value={dialog.frequency}
                    onChange={(e) => setDialog({ ...dialog, frequency: e.target.value })}>
                    {(enums.FREQUENCIES || []).map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth disabled={dialog.frequency !== 'weekly'}>
                  <InputLabel>Weekday</InputLabel>
                  <Select label="Weekday" value={dialog.weekday}
                    onChange={(e) => setDialog({ ...dialog, weekday: e.target.value })}>
                    <MenuItem value="">Any</MenuItem>
                    {WEEKDAYS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select label="Priority" value={dialog.priority}
                    onChange={(e) => setDialog({ ...dialog, priority: e.target.value })}>
                    {(enums.PRIORITIES || []).map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Brand pillar</InputLabel>
                  <Select label="Brand pillar" value={dialog.brand_pillar}
                    onChange={(e) => setDialog({ ...dialog, brand_pillar: e.target.value })}>
                    {['none', 'trust', 'profit', 'selection', 'support'].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField label="Est. min" type="number" sx={{ width: 130 }}
                  value={dialog.estimated_minutes}
                  onChange={(e) => setDialog({ ...dialog, estimated_minutes: e.target.value })} />
              </Stack>

              {dialog.type === 'dynamic' && (
                <>
                  <Divider textAlign="left"><Chip size="small" label="Data rule" /></Divider>
                  <FormControl fullWidth>
                    <InputLabel>Rule</InputLabel>
                    <Select label="Rule" value={dialog.rule_key}
                      onChange={(e) => {
                        const r = rules.find((x) => x.key === e.target.value);
                        setDialog({ ...dialog, rule_key: e.target.value, rule_params: JSON.stringify(r?.defaultParams || {}) });
                      }}>
                      {rules.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {dialog.rule_key && (
                    <Alert severity="info" icon={<Info size={16} />} sx={{ py: 0 }}>
                      {rules.find((r) => r.key === dialog.rule_key)?.description}
                    </Alert>
                  )}
                  <TextField label="Rule params (JSON)" fullWidth
                    value={dialog.rule_params}
                    onChange={(e) => setDialog({ ...dialog, rule_params: e.target.value })} />
                </>
              )}

              <FormControlLabel control={<Switch checked={dialog.active}
                onChange={(e) => setDialog({ ...dialog, active: e.target.checked })} />} label="Active" />
            </Stack>
          )}
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={() => setDialog(null)} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={save} disabled={!dialog?.title}
            sx={{ px: 3, fontWeight: 700 }}>
            {dialog?._id ? 'Save Changes' : 'Create Template'}
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        itemName={deleteTarget?.title}
        itemType="SOP Template"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

/* ─────────────────────────── Page shell ───────────────────────────────────*/
const SopView = () => {
  const [tab, setTab] = useState(0);
  const date = todayYmd();

  return (
    <Box>
      <PageHeader
        title="SOPs & Daily Operating Routines"
        subtitle="Daily & weekly operating routines — part fixed checklist, part auto-generated from your live data."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Operations', href: '/sop' },
          { label: 'SOP Routines' }
        ]}
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2,
        '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 14 } }}>
        <Tab label="Today" />
        <Tab label="This Week" />
        <Tab label="Templates" />
      </Tabs>

      {tab === 0 && <ChecklistTab scope="day" date={date} />}
      {tab === 1 && <ChecklistTab scope="week" date={date} />}
      {tab === 2 && <TemplatesTab />}
    </Box>
  );
};

export default SopView;
